import { useEffect, useMemo, useState } from 'react'
import { Ban, Loader2, UserX, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  cancelAppointment,
  fetchCancellationReasons,
  markAppointmentNoShow,
} from '@/lib/api/appointments'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import {
  canCancelAppointment,
  canMarkAppointmentNoShow,
} from '@/lib/calendar/cancellationReasons'
import { formatArrivalTime } from '@/lib/calendar/checkIn'
import { cn } from '@/lib/utils'

/**
 * @param {{
 *   open: boolean
 *   appointment: import('@/types/api').Appointment | null
 *   onClose: () => void
 *   onUpdated?: () => void | Promise<void>
 * }} props
 */
export function AppointmentOutcomeModal({ open, appointment, onClose, onUpdated }) {
  const [cancelReasons, setCancelReasons] = useState([])
  const [noShowReasons, setNoShowReasons] = useState([])
  const [loadingReasons, setLoadingReasons] = useState(false)
  const [cancelReasonCode, setCancelReasonCode] = useState('')
  const [noShowReasonCode, setNoShowReasonCode] = useState('')
  const [pendingAction, setPendingAction] = useState(null)
  const [error, setError] = useState('')

  const showCancel = appointment ? canCancelAppointment(appointment) : false
  const showNoShow = appointment ? canMarkAppointmentNoShow(appointment) : false

  useEffect(() => {
    if (!open) {
      return
    }

    let cancelled = false
    setLoadingReasons(true)
    setError('')

    async function loadReasons() {
      try {
        const [cancelItems, noShowItems] = await Promise.all([
          fetchCancellationReasons('cancel'),
          fetchCancellationReasons('no_show'),
        ])

        if (!cancelled) {
          setCancelReasons(cancelItems)
          setNoShowReasons(noShowItems)
          setCancelReasonCode(cancelItems[0]?.value ?? '')
          setNoShowReasonCode(noShowItems[0]?.value ?? '')
        }
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, 'Unable to load reason codes.'))
        }
      } finally {
        if (!cancelled) {
          setLoadingReasons(false)
        }
      }
    }

    loadReasons()

    return () => {
      cancelled = true
    }
  }, [open])

  const guestLabel = useMemo(() => {
    if (!appointment) {
      return 'Guest'
    }

    return appointment.customer?.name ?? 'Guest'
  }, [appointment])

  if (!open || !appointment) {
    return null
  }

  async function handleCancel() {
    if (!cancelReasonCode) {
      setError('Select a cancellation reason.')
      return
    }

    setPendingAction('cancel')
    setError('')

    try {
      await cancelAppointment(appointment.id, { cancellation_reason_code: cancelReasonCode })
      await onUpdated?.()
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to cancel appointment.'))
    } finally {
      setPendingAction(null)
    }
  }

  async function handleNoShow() {
    if (!noShowReasonCode) {
      setError('Select a no-show reason.')
      return
    }

    setPendingAction('no_show')
    setError('')

    try {
      await markAppointmentNoShow(appointment.id, {
        cancellation_reason_code: noShowReasonCode,
      })
      await onUpdated?.()
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to mark no-show.'))
    } finally {
      setPendingAction(null)
    }
  }

  const busy = pendingAction !== null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="w-full max-w-lg rounded-2xl border bg-card shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-outcome-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/60 px-5 py-4">
          <div>
            <h2 id="appointment-outcome-title" className="text-base font-semibold text-foreground">
              Cancel or no-show
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {guestLabel} · {formatArrivalTime(appointment.starts_at)}
              {appointment.services?.[0]?.service?.name
                ? ` · ${appointment.services[0].service.name}`
                : ''}
            </p>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {loadingReasons ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading reason codes…
            </div>
          ) : (
            <>
              {showCancel ? (
                <div className="space-y-2 rounded-xl border border-border/60 bg-muted/20 p-4">
                  <label
                    htmlFor="cancel-reason"
                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Cancel appointment
                  </label>
                  <select
                    id="cancel-reason"
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    value={cancelReasonCode}
                    disabled={busy}
                    onChange={(event) => setCancelReasonCode(event.target.value)}
                  >
                    {cancelReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-red-200 text-red-700 hover:bg-red-50"
                    disabled={busy}
                    onClick={handleCancel}
                  >
                    {pendingAction === 'cancel' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Ban className="mr-2 h-4 w-4" />
                    )}
                    Cancel booking
                  </Button>
                </div>
              ) : null}

              {showNoShow ? (
                <div
                  className={cn(
                    'space-y-2 rounded-xl border border-border/60 bg-muted/20 p-4',
                    showCancel && 'border-amber-200/80',
                  )}
                >
                  <label
                    htmlFor="no-show-reason"
                    className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    Mark no-show
                  </label>
                  <select
                    id="no-show-reason"
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    value={noShowReasonCode}
                    disabled={busy}
                    onChange={(event) => setNoShowReasonCode(event.target.value)}
                  >
                    {noShowReasons.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-amber-200 text-amber-900 hover:bg-amber-50"
                    disabled={busy}
                    onClick={handleNoShow}
                  >
                    {pendingAction === 'no_show' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserX className="mr-2 h-4 w-4" />
                    )}
                    Mark no-show
                  </Button>
                </div>
              ) : null}

              {!showCancel && !showNoShow ? (
                <p className="py-4 text-sm text-muted-foreground">
                  This appointment can no longer be cancelled or marked as no-show.
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
